/**
 * @file rawData.js
 * @description Contains static data for weather stations including metadata and configuration.
 * This file serves as a central repository for station information used throughout the application.
 */

/**
 * @typedef {Object} StationMetadata
 * @property {string} id - Unique identifier for the station
 * @property {string} name - Name of the station
 * @property {string} Community - Community or region where the station is located
 * @property {string} status - Current operational status of the station
 * @property {string} type - Type of weather station (e.g., WIRL_Weather_Station, ECCC_Weather_Station)
 * @property {string} location - Geographic location description
 * @property {number} default_latitude - Default latitude coordinate
 * @property {number} default_longitude - Default longitude coordinate
 * @property {boolean} weather_forecast_available - Whether Environment Canada weather forecast is available for this location
 * @property {string} acknowledgement - Acknowledgement text for the station
 */

/**
 * Array of weather station metadata
 * Contains information about both WIRL (Weather Information and Research Laboratory) and ECCC (Environment and Climate Change Canada) stations
 * @type {StationMetadata[]}
 */

/**
 * Standard acknowledgement text for weather stations
 * @type {string}
 */

/*
 * VARIABLE USAGE GUIDE:
 *
 * 1. ACKNOWLEDGEMENT VARIABLES:
 *    - Used at the bottom of each station page to display data source attribution
 *    - Available options:
 *      * WIRL_ACKNOWLEDGEMENT: "This weather information is provided courtesy of the Water and Ice Research Laboratory, Carleton University"
 *      * ECCC_ACKNOWLEDGEMENT: "Data Source: Environment and Climate Change Canada"
 *      * NAVCANADA_ACKNOWLEDGEMENT: "Data Source: Environment and Climate Change Canada and Nav Canada"
 *      * NTI_ACKNOWLEDGEMENT: "" (empty string)
 *      * IGLOOLIK_ACKNOWLEDGEMENT: "This weather information is provided courtesy of Igloolik, Nunavut"
 *      * SILANIARVIIT_ACKNOWLEDGEMENT: ""
 *
 * 2. CATEGORY FIELD:
 *    - Used for filtering stations on the Map page (NorthernCanadaMap.js)
 *    - Available options:
 *      * "Government": ECCC and NavCanada weather stations
 *      * "Community-led": WIRL stations in communities (Arctic Bay, Pond Inlet, Igloolik, etc.)
 *      * "Research": WIRL stations for research purposes (Experimental Farm, Milne Ice Shelf, Purple Valley) & NTI as of now as well as category with empty ""
 *    - Implementation: Filter buttons in NorthernCanadaMap.js use this field for category-based filtering
 *
 * 3. TYPE FIELD:
 *    - Used in AllStationList.js for station categorization and display organization
 *    - Available options:
 *      * "WIRL_Weather_Station": Weather Information and Research Laboratory stations
 *      * "ECCC_Weather_Station": Environment and Climate Change Canada stations  
 *      * "NTI_Weather_Station": NTI (currently commented out)
 *    - Implementation: AllStationList.js uses this field to:
 *      * Put ECCC stations in "Government" category
 *      * Put NTI stations in "Research" category
 *      * WIRL stations are grouped by Community field (actual communities) or put in "Research" if Community is "WIRL" or empty
 *      * The grouping logic works as follows:
 *        * First, stations are filtered by Community field - stations with Community="WIRL", empty string, or null go to "Research"
 *        * Then, remaining WIRL stations are grouped by their actual Community names (e.g., "Arctic Bay", "Pond Inlet", "Igloolik")
 *        * ECCC stations (type="ECCC_Weather_Station") are all placed in "Government" category regardless of Community
 *        * NTI stations (type="NTI_Weather_Station") are all placed in "Research" category regardless of Community
 *      * This creates a hierarchical display: Community groups first, then "Research", then "Government"
 *  
 * 4. STATUS FIELD:
 *    - Used for operational status display and filtering
 *    - Available options:
 *      * "Operational": Station is currently working
 *      * "Non-Operational": Station is not working
 *    - Implementation: Used in map markers, station pages, and operational status filtering
 *
 * 5. FORECAST_URL FIELD:
 *    - Environment Canada weather forecasts for each station location (not used for now - just put it here for reference ,in the actual station page when user clicks on the forcast link it will actually takes the lat and long from this file and generate the link)
 *    - Format: "https://weather.gc.ca/en/location/index.html?coords={latitude},{longitude}"
 
 *
 * 6. WEATHER_FORECAST_AVAILABLE FIELD:
 *    - Boolean field indicating if Environment Canada provides forecasts for this location
 *    - Used to determine whether to show forecast links and data
 *    - Implementation: Controls forecast availability display in station pages
 */
export const WIRL_ACKNOWLEDGEMENT =
  "This weather information is provided courtesy of the Water and Ice Research Laboratory, Carleton University";

export const ECCC_ACKNOWLEDGEMENT =
  "Data Source: Environment and Climate Change Canada";

export const NAVCANADA_ACKNOWLEDGEMENT =
  "Data Source: Environment and Climate Change Canada and Nav Canada";

export const NTI_ACKNOWLEDGEMENT = "";

export const IGLOOLIK_ACKNOWLEDGEMENT =
  "This weather information is provided courtesy of Igloolik, Nunavut";

export const SILANIARVIIT_ACKNOWLEDGEMENT = " ";

export const SANIRAJAK_ACKNOWLEDGEMENT =
  'Sanirajak community weather data © 2026 by Hall Beach Hunters and Trappers Association is licensed under <a href="https://creativecommons.org/licenses/by-nc-sa/4.0/">CC BY-NC-SA 4.0</a><img src="https://mirrors.creativecommons.org/presskit/icons/cc.svg" alt="" style="max-width: 1em;max-height:1em;margin-left: .2em;"><img src="https://mirrors.creativecommons.org/presskit/icons/by.svg" alt="" style="max-width: 1em;max-height:1em;margin-left: .2em;"><img src="https://mirrors.creativecommons.org/presskit/icons/nc.svg" alt="" style="max-width: 1em;max-height:1em;margin-left: .2em;"><img src="https://mirrors.creativecommons.org/presskit/icons/sa.svg" alt="" style="max-width: 1em;max-height:1em;margin-left: .2em;">';

export const stationMetaData = [
  {
    id: "JUV",
    name: "Qinngusaa",
    Community: "Sanirajak",
    status: "Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 68.7818583,
    default_longitude: -80.027935,
    weather_forecast_available: true,
    acknowledgement: SANIRAJAK_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords= 68.7818583,-80.027935",
  },
  {
    id: "GQC",
    name: "Iqalugaarjuk",
    Community: "Sanirajak",
    status: "Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 68.4472083,
    default_longitude: -82.6392083,
    weather_forecast_available: true,
    acknowledgement: SANIRAJAK_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords= 68.4472083,-82.6392083",
  },
  {
    id: "PBR",
    name: "Nunngarut",
    Community: "Iqaluit",
    status: "Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 63.60635819,
    default_longitude: -68.82166214,
    weather_forecast_available: true,
    acknowledgement: SILANIARVIIT_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=63.606358,-68.821662 ",
  },
  {
    id: "BQF",
    name: "Tasiluk",
    Community: "Iqaluit",
    status: "Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 63.864619,
    default_longitude: -68.488064,
    weather_forecast_available: true,
    acknowledgement: SILANIARVIIT_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=63.864619,-68.488064 ",
  },
  {
    id: "MPC",
    name: "Innaarulaalikuluup Nuvua",
    Community: "Arctic Bay",
    status: "Non-Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 73.52923,
    default_longitude: -85.44611,
    weather_forecast_available: true,
    acknowledgement: SILANIARVIIT_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=73.52923,-85.44611 ",
  },
  {
    id: "TST",
    name: "Experimental Farm",
    Community: "WIRL", //research
    status: "Operational",
    type: "WIRL_Weather_Station",
    location: "Ottawa",
    default_latitude: 45.382576,
    default_longitude: -75.714455,
    weather_forecast_available: true,
    acknowledgement: WIRL_ACKNOWLEDGEMENT,
    category: "Research",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=45.382576,-75.714455 ",
  },
  {
    id: "AWSV2",
    name: "Version 2 AWS",
    Community: "WIRL", //research
    status: "Operational",
    type: "WIRL_Weather_Station",
    location: "Ottawa",
    default_latitude: 45.382576,
    default_longitude: -75.714455,
    weather_forecast_available: true,
    acknowledgement: WIRL_ACKNOWLEDGEMENT,
    category: "Research",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=45.382576,-75.714455 ",
  },
  {
    id: "DVD",
    name: "Ausuittuq",
    Community: "Grise Fiord",
    status: "Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 76.42336,
    default_longitude: -82.91111,
    weather_forecast_available: true,
    acknowledgement: SILANIARVIIT_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=76.42336,-82.91111 ",
  },
  {
    id: "LOA",
    name: "Tuqqajaat",
    Community: "Pond Inlet",
    status: "Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 72.48496,
    default_longitude: -79.75987,
    weather_forecast_available: false,
    acknowledgement: SILANIARVIIT_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=72.48496,-79.75987 ",
  },
  {
    id: "VKP",
    name: "Sannirut",
    Community: "Pond Inlet",
    status: "Non-Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 72.850067,
    default_longitude: -76.120761,
    weather_forecast_available: true,
    acknowledgement: SILANIARVIIT_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=72.850067,-76.120761 ",
  },
  {
    id: "VKQ",
    name: "Iglukisaat",
    Community: "Pond Inlet",
    status: "Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 72.89004,
    default_longitude: -78.31232,
    weather_forecast_available: false,
    acknowledgement: SILANIARVIIT_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=72.89004,-78.31232 ",
  },
  {
    id: "ALW",
    name: "Aqiggilik",
    Community: "Arctic Bay",
    status: "Non-Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 73.560873,
    default_longitude: -83.761054,
    weather_forecast_available: true,
    acknowledgement: SILANIARVIIT_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=73.560873,-83.7610",
  },
  {
    id: "TDY",
    name: "Ivisaaruqtuuq",
    Community: "Igloolik",
    status: "Non-Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 70.136567,
    default_longitude: -82.216802,
    weather_forecast_available: true,
    acknowledgement: IGLOOLIK_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=70.136567,-82.216802 ",
  },
  {
    id: "UBY",
    name: "Kapuiviit Nuvua",
    Community: "Igloolik",
    status: "Non-Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 69.57053,
    default_longitude: -80.29736,
    weather_forecast_available: true,
    acknowledgement: IGLOOLIK_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=69.57053,-80.29736 ",
  },
  {
    id: "OEG",
    name: "Qalirusiq",
    Community: "Igloolik",
    status: "Non-Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 69.3909,
    default_longitude: -81.79963,
    weather_forecast_available: true,
    acknowledgement: IGLOOLIK_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=69.3909,-81.79963 ",
  },
  {
    id: "LVY",
    name: "Kalliraaq",
    Community: "Igloolik",
    status: "Non-Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 69.85861,
    default_longitude: -85.54392,
    weather_forecast_available: false,
    acknowledgement: IGLOOLIK_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=69.85861,-85.54392 ",
  },
  {
    id: "ZTJ",
    name: "Nuvuit",
    Community: "Igloolik",
    status: "Non-Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 69.82437,
    default_longitude: -77.67336,
    weather_forecast_available: false,
    acknowledgement: IGLOOLIK_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=69.82437,-77.67336 ",
  },
  {
    id: "FON",
    name: "Milne Ice Shelf",
    Community: "WIRL", //research
    status: "Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 82.71045,
    default_longitude: -81.2807,
    weather_forecast_available: false,
    acknowledgement: WIRL_ACKNOWLEDGEMENT,
    category: "Research",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=82.71045,-81.2807 ",
  },
  {
    id: "VUU",
    name: "Qikiqtatannak",
    Community: "Arctic Bay",
    status: "Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 72.756146,
    default_longitude: -85.91475,
    weather_forecast_available: true,
    acknowledgement: SILANIARVIIT_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=72.756146,-85.91475 ",
  },
  {
    id: "NPK",
    name: "Purple Valley",
    Community: "WIRL", //research
    status: "Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 82.462486,
    default_longitude: -80.736578,
    weather_forecast_available: false,
    acknowledgement: WIRL_ACKNOWLEDGEMENT,
    category: "Research",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=82.462486,-80.736578 ",
  },
  {
    id: "YBL",
    name: "Naujaaruluit",
    Community: "Igloolik",
    status: "Non-Operational",
    type: "WIRL_Weather_Station",
    location: "Nunavut",
    default_latitude: 69.825861,
    default_longitude: -82.139611,
    weather_forecast_available: true,
    acknowledgement: IGLOOLIK_ACKNOWLEDGEMENT,
    category: "Community-led",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=69.825861,-82.139611 ",
  },
  {
    id: "CXAT",
    name: "Arctic Bay",
    Community: "ᐃᒃᐱᐊᕐᔪᒃ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 72.99275,
    default_longitude: -85.011575,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=72.99275,-85.011575 ",
  },
  {
    id: "CNDT",
    name: "Pond Inlet",
    Community: "ᒥᑦᑎᒪᑕᓕᒃ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 72.69313,
    default_longitude: -77.95735,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=72.69313,-77.95735 ",
  },
  {
    id: "CNPL",
    name: "Cape Liverpool",
    Community: "Pond Inlet",
    status: "Non-Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 73.668041,
    default_longitude: -78.294694,
    weather_forecast_available: false,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=73.668041,-78.294694 ",
  },
  {
    id: "CXUX",
    name: "Sanirajak",
    Community: "ᓴᓂᕋᔭᒃ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 68.775086,
    default_longitude: -81.228675,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=68.775086,-81.228675 ",
  },
  {
    id: "CZLT",
    name: "Alert",
    Community: "Alert",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 82.493841,
    default_longitude: -62.352153,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=82.493841,-62.352153 ",
  },
  {
    id: "CNSG",
    name: "Svartevaeg",
    Community: "Svartevaeg",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 81.161196,
    default_longitude: -91.816114,
    weather_forecast_available: false,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=81.161196,-91.816114 ",
  },
  {
    id: "CWEU",
    name: "Eureka",
    Community: "Eureka",
    status: "Operational",
    type: "ECCC_Weather_Station", //NAVCANADA
    location: "Nunavut",
    default_latitude: 79.9944,
    default_longitude: -85.8119,
    weather_forecast_available: true,
    acknowledgement: NAVCANADA_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=79.9944,-85.8119 ",
  },
  // Commented out as it is being overlapped in the map with other station
  // {
  //   id: "CNGC",
  //   name: "Grise Fiord",
  //   Community: "ᐊᐅᔪᐃᑦᑐᖅ",
  //   status: "Operational",
  //   type: "ECCC_Weather_Station",
  //   location: "Nunavut",
  //   default_latitude: 76.422476,
  //   default_longitude: -82.90115,
  //   weather_forecast_available: true,
  //   acknowledgement: ECCC_ACKNOWLEDGEMENT,
  // },
  {
    id: "CZRP",
    name: "Rea Point",
    Community: "Rea Point",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 75.359453,
    default_longitude: -105.711865,
    weather_forecast_available: false,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=75.359453,-105.711865 ",
  },
  {
    id: "CYRB",
    name: "Resolute Bay",
    Community: "ᖃᐅᓱᐃᑦᑐᖅ",
    status: "Operational",
    type: "ECCC_Weather_Station", //NAVCANADA
    location: "Nunavut",
    default_latitude: 74.7169,
    default_longitude: -94.9693,
    weather_forecast_available: true,
    acknowledgement: NAVCANADA_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=74.7169,-94.9693 ",
  },
  {
    id: "CXSE",
    name: "Steffansson Island",
    Community: "Steffansson Island",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 73.765789,
    default_longitude: -105.295426,
    weather_forecast_available: false,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=73.765789,-105.295426 ",
  },
  {
    id: "CNFR",
    name: "Fort Ross",
    Community: "Fort Ross",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 72.018061,
    default_longitude: -94.200462,
    weather_forecast_available: false,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=72.018061,-94.200462 ",
  },
  {
    id: "CZCY",
    name: "Clyde River",
    Community: "ᑲᖏᖅᑐᒑᐱᒃ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 70.48383,
    default_longitude: -68.514889,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=70.48383,-68.514889 ",
  },
  {
    id: "CYYH",
    name: "Taloyoak",
    Community: "ᑕᓗᕐᔪᐊᖅ",
    status: "Operational",
    type: "ECCC_Weather_Station", //NAVCANADA
    location: "Nunavut",
    default_latitude: 69.5467,
    default_longitude: -93.577,
    weather_forecast_available: true,
    acknowledgement: NAVCANADA_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=69.5467,-93.577 ",
  },
  {
    id: "CYGT",
    name: "Igloolik",
    Community: "ᐃᒡᓗᓕᒃ",
    status: "Operational",
    type: "ECCC_Weather_Station", //NAVCANADA
    location: "Nunavut",
    default_latitude: 69.3647,
    default_longitude: -81.8165,
    weather_forecast_available: true,
    acknowledgement: NAVCANADA_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=69.3647,-81.8165 ",
  },
  {
    id: "CXCM",
    name: "Cambridge Bay",
    Community: "ᐃᖃᓗᒃᑑᑦᑎᐊᖅ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 69.106583,
    default_longitude: -105.115652,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=69.106583,-105.115652 ",
  },
  {
    id: "CPIN",
    name: "Bernard Harbour",
    Community: "Bernard Harbour",
    status: "Non-Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 68.75528,
    default_longitude: -114.9397,
    weather_forecast_available: false,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=68.75528,-114.9397 ",
  },
  {
    id: "CZHK",
    name: "Gjoa Haven",
    Community: "ᐅᖅᓱᖅᑑᖅ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 68.63344,
    default_longitude: -95.85188,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=68.63344,-95.85188 ",
  },
  {
    id: "CNBB",
    name: "Kugaaruk",
    Community: "ᑰᒑᕐᔪᒃ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 68.53743,
    default_longitude: -89.788915,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=68.53743,-89.788915 ",
  },
  {
    id: "CNCO",
    name: "Kugluktuk",
    Community: "ᖁᕐᓗᖅᑐᖅ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 67.8172,
    default_longitude: -115.1353,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=67.8172,-115.1353 ",
  },
  {
    id: "CZVM",
    name: "Qikiqtarjuaq",
    Community: "ᕿᑭᖅᑕᕐᔪᐊᖅ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 67.547377,
    default_longitude: -64.033079,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=67.547377,-64.033079 ",
  },
  {
    id: "CNBI",
    name: "Bathurst Inlet",
    Community: "ᕿᙵᐅᓐ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 66.837724,
    default_longitude: -108.014693,
    weather_forecast_available: false,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=66.837724,-108.014693 ",
  },
  {
    id: "CYUT",
    name: "Naujaat",
    Community: "ᓇᐅᔮᑦ",
    status: "Operational",
    type: "ECCC_Weather_Station", //NAVCANADA
    location: "Nunavut",
    default_latitude: 66.5207,
    default_longitude: -86.2247,
    weather_forecast_available: true,
    acknowledgement: NAVCANADA_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=66.5207,-86.2247 ",
  },
  {
    id: "CWXP",
    name: "Pangnirtung",
    Community: "ᐸᖕᓂᖅᑑᖅ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 66.143403,
    default_longitude: -65.711348,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=66.143403,-65.711348 ",
  },
  {
    id: "CXWB",
    name: "Wager Bay",
    Community: "Wager Bay",
    status: "Non-Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 65.879305,
    default_longitude: -89.445688,
    weather_forecast_available: false,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=65.879305,-89.445688 ",
  },
  {
    id: "CWIJ",
    name: "Lupin",
    Community: "Lupin",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 65.755618,
    default_longitude: -111.245862,
    weather_forecast_available: false,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=65.755618,-111.245862 ",
  },
  {
    id: "CWTD",
    name: "Robertson Lake",
    Community: "Robertson Lake",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 65.099298,
    default_longitude: -102.429464,
    weather_forecast_available: false,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=65.099298,-102.429464 ",
  },
  {
    id: "CXBL",
    name: "Baker Lake",
    Community: "ᖃᒪᓂᑦᑐᐊᖅ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 64.318981,
    default_longitude: -96.001642,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=64.318981,-96.001642 ",
  },
  {
    id: "CNCD",
    name: "Kinngait",
    Community: "ᑭᙵᐃᑦ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 64.230626,
    default_longitude: -76.530694,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=64.230626,-76.530694 ",
  },
  {
    id: "CNZS",
    name: "Coral Harbour",
    Community: "ᓴᓪᓕᖅ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 64.187831,
    default_longitude: -83.347054,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=64.187831,-83.347054 ",
  },
  {
    id: "CXFB",
    name: "Iqaluit",
    Community: "ᐃᖃᓗᐃᑦ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 63.747809,
    default_longitude: -68.544293,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=63.747809,-68.544293 ",
  },
  {
    id: "CYCS",
    name: "Chesterfield Inlet",
    Community: "ᐃᒡᓗᓕᒑᕐᔪᒃ",
    status: "Operational",
    type: "ECCC_Weather_Station", //NAVCANADA
    location: "Nunavut",
    default_latitude: 63.3471,
    default_longitude: -90.7312,
    weather_forecast_available: true,
    acknowledgement: NAVCANADA_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=63.3471,-90.7312 ",
  },
  {
    id: "CYLC",
    name: "Kimmirut",
    Community: "ᑭᒻᒥᕈᑦ",
    status: "Operational",
    type: "ECCC_Weather_Station", //NAVCANADA
    location: "Nunavut",
    default_latitude: 62.8481,
    default_longitude: -69.8772,
    weather_forecast_available: true,
    acknowledgement: NAVCANADA_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=62.8481,-69.8772 ",
  },
  {
    id: "CYRT",
    name: "Rankin Inlet",
    Community: "ᑲᖏᕿᓂᖅ",
    status: "Non-Operational",
    type: "ECCC_Weather_Station", //NAVCANADA
    location: "Nunavut",
    default_latitude: 62.8107,
    default_longitude: -92.1146,
    weather_forecast_available: true,
    acknowledgement: NAVCANADA_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=62.8107,-92.1146 ",
  },
  {
    id: "CYXN",
    name: "Whale Cove",
    Community: "ᑎᑭᕋᕐᔪᐊᖅ",
    status: "Operational",
    type: "ECCC_Weather_Station", //NAVCANADA
    location: "Nunavut",
    default_latitude: 62.2401,
    default_longitude: -92.5979,
    weather_forecast_available: true,
    acknowledgement: NAVCANADA_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=62.2401,-92.5979 ",
  },
  {
    id: "CWJC",
    name: "Ennadai Lake",
    Community: "Ennadai Lake",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 61.131511,
    default_longitude: -100.883392,
    weather_forecast_available: false,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=61.131511,-100.883392 ",
  },
  {
    id: "CXAR",
    name: "Arviat",
    Community: "ᐊᕐᕕᐊᑦ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 61.098323,
    default_longitude: -94.068804,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=61.098323,-94.068804 ",
  },
  {
    id: "CNSQ",
    name: "Sanikiluaq",
    Community: "ᓴᓂᑭᓗᐊᖅ",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 56.5374,
    default_longitude: -79.2605,
    weather_forecast_available: true,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=56.5374,-79.2605 ",
  },
  {
    id: "CWIC",
    name: "Isachsen",
    Community: "Isachsen",
    status: "Operational",
    type: "ECCC_Weather_Station",
    location: "Nunavut",
    default_latitude: 78.793968,
    default_longitude: -102.471566,
    weather_forecast_available: false,
    acknowledgement: ECCC_ACKNOWLEDGEMENT,
    category: "Government",
    forecast_url:
      "https://weather.gc.ca/en/location/index.html?coords=78.793968,-102.471566",
  },

  // NTI Stations - Not in use in the app as of now,once we recieve the data from the NTI we can add them to the app
  // {
  //   id: "cove_weather_station_idartm1",
  //   name: "COVE Weather Station, IDARTM1",
  //   Community: "COVE",
  //   status: "Operational",
  //   type: "NTI_Weather_Station",
  //   location: "COVE",
  //   default_latitude: 44.660999,
  //   default_longitude: -63.556,
  //   weather_forecast_available: false,
  //   acknowledgement: NTI_ACKNOWLEDGEMENT,
  //   category: "Research",
  //   forecast_url: "https://weather.gc.ca/en/location/index.html?coords=44.660999,-63.556",
  // },
  // {
  //   id: "sma_negl_cartwright_junction_nlqu0004",
  //   name: "NEGL, Cartwright Junction, Labrador (NLQU0004)",
  //   Community: "Cartwright",
  //   status: "Operational",
  //   type: "NTI_Weather_Station",
  //   location: "Cartwright",
  //   default_latitude: 52.71983,
  //   default_longitude: -58.45314,
  //   weather_forecast_available: false,
  //   acknowledgement: NTI_ACKNOWLEDGEMENT,
  //   category: "Research",
  //   forecast_url: "https://weather.gc.ca/en/location/index.html?coords=52.71983,-58.45314",
  // },
  // {
  //   id: "SMA_Holyrood_Buoy2",
  //   name: "Holyrood Buoy 2",
  //   Community: "Holyrood Bay",
  //   status: "Operational",
  //   type: "NTI_Weather_Station",
  //   location: "Holyrood Bay",
  //   default_latitude: 47.461835,
  //   default_longitude: -53.10810666666667,
  //   weather_forecast_available: false,
  //   acknowledgement: NTI_ACKNOWLEDGEMENT,
  //   category: "Research",
  //   forecast_url: "https://weather.gc.ca/en/location/index.html?coords=47.461835,-53.10810666666667",
  // },
  // {
  //   id: "sma_negl_rigolet_nlqu0002",
  //   name: "NEGL, Rigolet, Nunatsiavut (NLQU0002)",
  //   Community: "Rigolet",
  //   status: "Operational",
  //   type: "NTI_Weather_Station",
  //   location: "Rigolet",
  //   default_latitude: 54.17608,
  //   default_longitude: -58.43936,
  //   weather_forecast_available: false,
  //   acknowledgement: NTI_ACKNOWLEDGEMENT,
  //   category: "Research",
  //   forecast_url: "https://weather.gc.ca/en/location/index.html?coords=54.17608,-58.43936",
  // },
];

export const flagTableHeaders = [
  "ID",
  "Station",
  "Variable",
  "From Date",
  "To Date",
  "QC Value",
  "Comment",
  "AWS Mapping ID",
];
